import { useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Menu, LogOut, User, Bell, Sun, Moon, Monitor, Search } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useAuth } from '@/features/auth/auth-context'
import { useProfile } from '@/features/profile/hooks/use-profile'
import { useNotifications } from '@/features/notifications/hooks/use-notifications'
import { getStoredThemeMode, setThemeMode, type ThemeMode } from '@/lib/theme'
import { IdLookupModal } from '@/features/profile/components/id-lookup-modal'

interface HeaderProps {
  onMenuToggle: () => void
  sidebarPinned: boolean
}

export function Header({ onMenuToggle, sidebarPinned }: HeaderProps) {
  const { user, signOut } = useAuth()
  const { profile } = useProfile()
  const { unreadCount } = useNotifications()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [showIdLookup, setShowIdLookup] = useState(false)
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    try {
      return getStoredThemeMode()
    } catch {
      return 'system'
    }
  })
  const menuRef = useRef<HTMLDivElement>(null)

  const toggleTheme = () => {
    // Cycle: light → dark → system → light
    const next: ThemeMode =
      themeMode === 'light' ? 'dark' : themeMode === 'dark' ? 'system' : 'light'
    try {
      setThemeMode(next)
    } catch {
      // localStorage may not be available in test environments
    }
    setThemeModeState(next)
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSignOut = async () => {
    setMenuOpen(false)
    await signOut()
    navigate('/auth')
  }

  const displayName = user?.user_metadata?.name || user?.email?.split('@')[0] || 'User'
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
  // DB returns snake_case, TS type is camelCase — handle both
  const avatarUrl =
    profile?.profilePhoto ||
    ((profile as unknown as Record<string, unknown>)?.profile_photo as string | undefined)

  return (
    <>
      <header className="border-b border-gray-200 bg-white dark:border-white/5 dark:bg-[#0f0f0f]">
        {/* Thin brand gradient accent strip */}
        <div
          className="h-0.5"
          style={{
            background:
              'linear-gradient(90deg, #7c3aed 0%, #a855f7 30%, #db2777 60%, #3b82f6 100%)',
          }}
        />
        <div className="flex h-16 items-center px-4 lg:px-6">
          <div className={cn('flex items-center gap-2', sidebarPinned ? 'hidden' : 'lg:hidden')}>
            <button
              onClick={onMenuToggle}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10"
              aria-label="Toggle menu"
            >
              <Menu className="h-6 w-6" />
            </button>
            <span className="text-lg font-bold text-gray-900 dark:text-white">Zero</span>
          </div>
          <div className="ml-auto flex items-center gap-4">
            <button
              onClick={() => setShowIdLookup(true)}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10"
              aria-label="Lookup shooter"
              title="Lookup Shooter"
            >
              <Search className="h-5 w-5" />
            </button>
            <button
              onClick={toggleTheme}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10"
              aria-label="Toggle theme"
              title={
                themeMode === 'light'
                  ? 'Switch to Dark Mode'
                  : themeMode === 'dark'
                    ? 'Switch to System Mode'
                    : 'Switch to Light Mode'
              }
            >
              {themeMode === 'dark' ? (
                <Sun className="h-5 w-5" />
              ) : themeMode === 'system' ? (
                <Monitor className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </button>
            <Link
              to="/notifications"
              className="relative rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10"
              aria-label="Notifications"
              title="Notifications"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={displayName}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-zero-100 text-xs font-semibold text-zero-700">
                    {initials}
                  </span>
                )}
                <span className="hidden sm:inline">{displayName}</span>
              </button>

              {menuOpen && (
                <div className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-white/10 dark:bg-[#171717]">
                  <button
                    onClick={() => {
                      setMenuOpen(false)
                      navigate('/profile')
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/5"
                  >
                    <User className="h-4 w-4" />
                    Profile
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-50 dark:text-red-400 dark:hover:bg-white/5"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Shooter ID Lookup Modal */}
      <IdLookupModal open={showIdLookup} onClose={() => setShowIdLookup(false)} />
    </>
  )
}
